// Map #8 Crystal Desert, audio half. Moonlit dunes and humming crystal spires (night by default).
// Ambience: dry wind and hissing sand (both roar in the sandstorm), the slow beating hum of the crystal spires (stronger at
// night), crystal chimes, sand sliding off slip faces, distant dune rumbles; desert insects and a far jackal-like howl at
// night, a raptor by day. Clear, long echo.
// Music: A phrygian-dominant caravan, 104 bpm: a low drone, FM crystal-bell arps, a nasal reed lead and frame drums.
import { MODES, type MapAudioDef } from '../../audio/mapAudioDef';

export const AUDIO: MapAudioDef = {
  id: 'crystal-desert',
  ambience: {
    level: 1.4,
    beds: [
      { src: 'pink', filter: 'bandpass', freq: 650, q: 0.7, gain: 0.4, storm: 3, gust: { depth: 0.6, every: [3, 7], sweep: 1.6 } },
      { src: 'white', filter: 'bandpass', freq: 5500, q: 0.6, gain: 0.06, storm: 5, gust: { depth: 0.8, every: [1.5, 4] } },
      { src: { wave: 'sine', freqs: [220, 220.7, 329.6, 440.9] }, filter: 'lowpass', freq: 1200, gain: 0.018, day: 0.6, night: 1.4, gust: { depth: 0.5, every: [4, 9] } },
    ],
    events: [
      { kind: 'chime', every: [5, 11], gain: 0.07, voices: 2, night: 1.3, echo: 0.55 },
      { kind: 'gust', every: [7, 14], gain: 0.3, storm: 2 },
      { kind: 'hiss', every: [10, 20], gain: 0.07, pitch: 0.8 },
      { kind: 'cricket', every: [2.5, 5], gain: 0.035, day: 0, night: 1, voices: 2 },
      { kind: 'howl', every: [30, 60], gain: 0.06, day: 0, night: 1, pitch: 1.35, echo: 0.6 },
      { kind: 'raptor', every: [25, 45], gain: 0.07, day: 1, night: 0, echo: 0.4 },
      { kind: 'rumble', every: [25, 50], gain: 0.2, storm: 2 },
    ],
    echo: { l: 0.39, r: 0.58, fb: 0.45, damp: 2800, wet: 0.7 },
  },
  music: {
    mood: 'moonlit glass caravan (A phrygian dominant, 104 bpm)',
    score: {
      bpm: 104, steps: 16, root: 45, mode: MODES.phrygianDominant, progression: [0, 1, 0, 6, 0, 1, 5, 1],
      drone: { wave: 'sawtooth', notes: [0, 7], cutoff: 450, gain: 0.05, octave: 0 },
      pad: { wave: 'triangle', voices: 3, detune: 9, cutoff: 1600, gain: 0.06, octave: 1, send: 0.4 },
      bass: { wave: 'triangle', cutoff: 700, atk: 0.005, dec: 0.25, sus: 0.5, rel: 0.1, gain: 0.4, octave: 0,
        pattern: '1..1..1.5...1...', hot: '1..1..1.5.1.1.5.' },
      arp: { wave: 'sine', fm: { ratio: 3.5, index: 1.4, decay: 0.2 }, cutoff: 7000, atk: 0.002, dec: 0.5, sus: 0, rel: 0.3, gain: 0.08, octave: 2, send: 0.55,
        pattern: [0, -1, -1, 2, -1, -1, 1, -1, 0, -1, -1, 2, -1, 4, -1, -1], hot: [0, -1, 2, 1, -1, 2, 1, -1, 0, -1, 2, 1, -1, 4, 3, -1] },
      lead: { wave: 'square', cutoff: 1800, q: 3, atk: 0.03, dec: 0.3, sus: 0.75, rel: 0.2, gain: 0.07, octave: 1, send: 0.45,
        vibrato: { rate: 5.5, cents: 30 }, bars: [4, 6],
        motifs: [
          { notes: [[0, 2], [1, 2], [2, 4], [1, 2], [0, 2], [1, 4], [0, 8], [null, 8]] },
          { notes: [[4, 3], [5, 1], [4, 2], [2, 2], [1, 2], [2, 2], [0, 4], [-1, 4], [0, 4], [null, 8]] },
          { notes: [[2, 2], [3, 2], [4, 4], [5, 2], [4, 2], [2, 4], [1, 4], [0, 8], [null, 4]] },
        ] },
      drums: { kit: 'frame', gain: 0.9,
        main: { kick: 'X.....X...X.....', snare: '...x....x....x..', hat: 'x.g.x.g.x.g.x.g.' },
        fill: { snare: '...x....x.x.xxxx' },
        hot: { kick: 'X..x..X...X.x...', snare: '...x.gx.xg..x.gx', hat: 'xgxgxgxgxgxgxgxg', tom: '..............xx' } },
      echo: { steps: 3, fb: 0.38, wet: 0.25, damp: 3000 },
      level: 0.67,
    },
  },
};
